import React, {useState, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuthtoolContext} from '@mia-platform-internal/authtool-client-js/react';
import '../auth/auth.css';

/**
 * Register Page Component
 * Beautiful registration interface for new users
 */
function Register() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { loginUrl } = useAuthtoolContext();

  const handleRegister = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      
      window.location.href = loginUrl(window.location.href).href;
      
    } catch (err) {
      setIsLoading(false);
      setError(err.message || 'Failed to start registration');
      // eslint-disable-next-line no-console
      console.error('Registration error:', err);
    }
  }, [loginUrl]);

  return (
    <div className="auth-page-container">
      <div className="auth-background">
        <div className="gradient-orb gradient-orb-1"></div>
        <div className="gradient-orb gradient-orb-2"></div>
        <div className="gradient-orb gradient-orb-3"></div>
      </div>
      
      <div className="auth-content">
        <div className="auth-card">
          {/* Logo Section */}
          <div className="auth-logo">
            <div className="logo-circle">
              <svg className="logo-svg" viewBox="0 0 100 100">
                <path
                  className="logo-path"
                  d="M50 10 L90 30 L90 70 L50 90 L10 70 L10 30 Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                />
                <circle className="logo-dot" cx="50" cy="50" fill="currentColor" r="15" />
              </svg>
            </div>
            <h1 className="auth-title">{'Flow'}</h1>
            <p className="auth-subtitle">{'Turn thoughts into code.'}</p>
          </div>

          {/* Registration Form */}
          <div className="auth-form">
            <h2 className="form-title">{'Create Your Account'}</h2>
            <p className="form-description">
              {'Join the next generation of intelligent development'}
            </p>

            {error && (
              <div className="auth-error">
                <svg className="error-icon" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" fill="none" r="9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M10 6 L10 11" stroke="currentColor" strokeLinecap="round" strokeWidth="2"/>
                  <circle cx="10" cy="14" fill="currentColor" r="1"/>
                </svg>
                {error}
              </div>
            )}

            <button
              className="auth-button"
              disabled={isLoading}
              onClick={handleRegister}
            >
              {isLoading ? (
                <>
                  <span className="button-spinner"></span>
                  <span>{'Setting up your account...'}</span>
                </>
              ) : (
                <>
                  <svg className="button-icon" viewBox="0 0 20 20">
                    <path
                      d="M10 2 L10 18 M2 10 L18 10"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                  <span>{'Create Account with Mia Platform'}</span>
                </>
              )}
            </button>

            <div className="auth-divider">
              <span>{'or'}</span>
            </div>

            <button
              className="auth-button-secondary"
              disabled={isLoading}
              onClick={() => navigate('/login')}
            >
              {'Already have an account? Sign in'}
            </button>
          </div>

          {/* Benefits Section */}
          <div className="auth-benefits">
            <h3 className="benefits-title">{'What you get with Flow'}</h3>
            <div className="benefit-list">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg viewBox="0 0 20 20">
                    <path
                      d="M10 2 L3 7 L3 17 L10 12 L17 17 L17 7 Z"
                      fill="currentColor"
                      opacity="0.2"
                    />
                    <path
                      d="M10 2 L3 7 M10 2 L17 7 M3 17 L10 12 M10 12 L17 17 M3 7 L3 17 M17 7 L17 17"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
                <div className="benefit-content">
                  <h4>{'AI Code Generation'}</h4>
                  <p>{'Generate production-ready code with intelligent AI assistance'}</p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg viewBox="0 0 20 20">
                    <rect fill="currentColor" height="14" opacity="0.2" rx="2" width="14" x="3" y="3"/>
                    <rect fill="none" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" width="14" x="3" y="3"/>
                    <path d="M7 10 L9 12 L13 8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div className="benefit-content">
                  <h4>{'Project Management'}</h4>
                  <p>{'Seamlessly manage tenants, projects, and deployments'}</p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg viewBox="0 0 20 20">
                    <circle cx="10" cy="10" fill="currentColor" opacity="0.2" r="8"/>
                    <circle cx="10" cy="10" fill="none" r="8" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10 6 L10 10 L14 10" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div className="benefit-content">
                  <h4>{'Real-Time Updates'}</h4>
                  <p>{'Live collaboration with instant synchronization'}</p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg viewBox="0 0 20 20">
                    <path
                      d="M10 2 L12 8 L18 8 L13 12 L15 18 L10 14 L5 18 L7 12 L2 8 L8 8 Z"
                      fill="currentColor"
                      opacity="0.2"
                    />
                    <path
                      d="M10 2 L12 8 L18 8 L13 12 L15 18 L10 14 L5 18 L7 12 L2 8 L8 8 Z"
                      fill="none"
                      stroke="currentColor"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
                <div className="benefit-content">
                  <h4>{'Enterprise Features'}</h4>
                  <p>{'SSO, audit logs, and advanced security controls'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="auth-footer">
            <p>
              {'By creating an account, you agree to our '}
              <a href="https://www.mia-platform.eu/terms" rel="noopener noreferrer" target="_blank">
                {'Terms of Service'}
              </a>
              {' and '}
              <a href="https://www.mia-platform.eu/privacy" rel="noopener noreferrer" target="_blank">
                {'Privacy Policy'}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
